import { useState } from 'react'

import { Menu, MenuItem } from '@/components/interactives/dropdown-menu'

import type { TimingFix } from './timingFixTypes'

type DisabledFixProps = {
  label: string
  hint?: string
  disabled?: boolean
}

/**
 * An unavailable fix, shown greyed out with its reason on hover.
 *
 * NOT a `disabled` MenuItem: that button carries `disabled:pointer-events-none`
 * (and `disabled:-z-10`), so it never receives a hover and its `title`, the
 * only thing explaining WHY the fix is unavailable, can never appear. Since
 * the whole point of listing an unavailable fix is to teach the user why it
 * doesn't apply, the item stays hoverable and is made inert by styling and
 * `aria-disabled` instead.
 */
function DisabledFix({ label, hint, ...rest }: DisabledFixProps) {
  // Menu reads `disabled` off this element (child.props.disabled) to skip it in
  // keyboard navigation, so the call site must pass it, but it must not reach
  // the div: the real attribute is what kills the hover this component exists
  // to restore, and React warns about it on a <div> anyway.
  const injected = { ...rest, disabled: undefined }

  return (
    <div
      {...injected}
      role="menuitem"
      aria-disabled="true"
      title={hint}
      // Menu clones its children with an onClick that closes the whole dropdown
      // (`tree.events.emit('click')`). Swallow it here: clicking an unavailable
      // fix should leave the menu open, not dismiss it with nothing to show for
      // the click.
      onClick={(event) => event.stopPropagation()}
      // Mirrors MenuItem's disabled look (h-7/pr-3/pl-3, opacity-40) minus the
      // pointer-events + z-index rules that would swallow the hover.
      className="flex items-center rounded-sm h-7 pr-3 pl-3 text-left text-white font-light text-sm opacity-40 cursor-default"
    >
      <span className="grow">{label}</span>
    </div>
  )
}

/**
 * Render one fix descriptor: a leaf `MenuItem`, or a nested `Menu` when the fix
 * carries a `submenu` (e.g. "Adjust a cue above ▸" listing every cue above the
 * anchor). A nested `<Menu>` inside a `<Menu>` renders as a submenu (see
 * dropdown-menu.tsx `useFloatingParentNodeId`).
 */
function renderFix(fix: TimingFix) {
  // Disabled submenu groups and leaves alike: render the hoverable stand-in so
  // the hint survives. A disabled group has no reachable children anyway.
  if (fix.disabled) {
    return (
      <DisabledFix
        key={fix.key}
        label={fix.label}
        hint={fix.hint}
        disabled
      />
    )
  }

  if (fix.submenu) {
    // The nested-menu trigger has only `pr-3` (no left padding, no icon slot),
    // so add `pl-3` to align its label with the leaf MenuItems above it.
    return (
      <Menu
        key={fix.key}
        label={fix.label}
        className="pl-3"
      >
        {fix.submenu.map(renderFix)}
      </Menu>
    )
  }

  return (
    <MenuItem
      key={fix.key}
      label={fix.label}
      title={fix.detail}
      onClick={() => fix.onSelect?.()}
    />
  )
}

type TimingFixMenuProps = {
  type: 'Gap' | 'Overlap'
  buildFixes: () => TimingFix[]
}

/**
 * "Fix ▾" dropdown shown next to a gap/overlap on a hard-start cue.
 *
 * Purely presentational: `computeTimingFixes` computes each fix's new field
 * value and label, and CueItem binds those to mutations before they get here.
 * Every item states the concrete result inline (e.g. "Shift show start →
 * 09:55:00") so the click is never blind. A fix may be a leaf action or a group
 * with a `submenu` of sub-items. An unavailable fix arrives with
 * `disabled: true` and an explanatory `hint`.
 *
 * Fixes arrive as a THUNK, not a list. Planning them runs createTimestamps plus
 * one planBacktime per cue above the anchor, so building the list eagerly would
 * repeat that on every render of every gap cue, ~3x/sec during a live show. The
 * thunk is called when the user reaches for the menu, and the result is held
 * until the trigger unmounts.
 */
export function TimingFixMenu({ type, buildFixes }: TimingFixMenuProps) {
  const [fixes, setFixes] = useState<TimingFix[] | null>(null)

  // Plan on intent rather than on open, so the items are ready by the time the
  // panel paints. Recomputed each time: cached fixes would go stale as the
  // timeline advances underneath. `onPointerDown` covers mouse and touch and
  // `onFocus` the keyboard. Note `Menu` swallows `onClick` (it defines its own
  // that only calls stopPropagation), so a click handler here would never run.
  const plan = () => setFixes(buildFixes())

  return (
    <Menu
      label="Fix"
      placement="bottom-start"
      // Compact, understated button: shrink the base trigger's height/padding
      // and soften its background so it reads as a small chip next to the timing
      // indicator rather than a full-size menu button. Tailwind v4 orders
      // classes by its own rules (not source order), so overrides need `!`.
      className="h-6! px-2! gap-1.5 text-xs! font-medium! bg-white/5! hover:enabled:bg-white/10! text-gray-300!"
      aria-label={`Fix ${type.toLowerCase()}`}
      onPointerEnter={plan}
      onPointerDown={plan}
      onFocus={plan}
    >
      {(fixes ?? []).map(renderFix)}
    </Menu>
  )
}
